/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * User Details Modal Component
 * Modal for viewing user details, organizations, sessions, and impersonation
 */

import { useState, useEffect } from 'react'
import {
  X,
  User,
  Mail,
  Building2,
  Shield,
  Clock,
  Globe,
  Monitor,
  Trash2,
  UserCog,
  Loader2,
  AlertTriangle,
  Calendar,
  LogIn,
  MapPin,
  KeyRound,
  RefreshCw,
  Copy,
  Check
} from 'lucide-react'
import toast from 'react-hot-toast'
import { getCountryFlag, getGeoDisplayInfo, formatIpAddress } from '../../utils/geoUtils'
import { createLogger } from '../../utils/logger'

const logger = createLogger('UserDetailsModal')

// Generate a strong, human-shareable password.
// Excludes visually ambiguous characters (0/O, 1/l/I) and guarantees at least
// one lowercase, uppercase, digit, and symbol so it always passes common policies.
const generatePassword = (length = 16) => {
  const lower = 'abcdefghijkmnpqrstuvwxyz'
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const digits = '23456789'
  const symbols = '!@#$%^&*-_+='
  const all = lower + upper + digits + symbols

  const randomInt = (max) => {
    const arr = new Uint32Array(1)
    window.crypto.getRandomValues(arr)
    return arr[0] % max
  }
  const pick = (set) => set[randomInt(set.length)]

  // Guarantee one of each class, then fill the rest
  const chars = [pick(lower), pick(upper), pick(digits), pick(symbols)]
  for (let i = chars.length; i < length; i++) {
    chars.push(pick(all))
  }

  // Fisher-Yates shuffle so the guaranteed chars aren't always in front
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomInt(i + 1)
    ;[chars[i], chars[j]] = [chars[j], chars[i]]
  }
  return chars.join('')
}

// Inline component to show user tag badges
const UserTagBadges = ({ userId }) => {
  const [tags, setTags] = useState([])

  useEffect(() => {
    if (!userId) return
    const fetchTags = async () => {
      try {
        const { userTagService } = await import('../../services/userTagService')
        const data = await userTagService.getUserTags(userId)
        setTags(data.tags || [])
      } catch {
        // Silently ignore - tags are optional UI
      }
    }
    fetchTags()
  }, [userId])

  if (tags.length === 0) return null

  return tags.map((tag) => (
    <span
      key={tag.id}
      className="px-2 py-1 text-xs font-medium rounded"
      style={{
        backgroundColor: tag.color ? `${tag.color}20` : '#6366f120',
        color: tag.color || '#6366f1',
        border: `1px solid ${tag.color || '#6366f1'}40`
      }}
    >
      {tag.name}
    </span>
  ))
}

const UserDetailsModal = ({ isOpen, onClose, userId, onImpersonate }) => {
  const [loading, setLoading] = useState(true)
  const [userData, setUserData] = useState(null)
  const [error, setError] = useState(null)
  const [revokingSession, setRevokingSession] = useState(null)
  const [revokingAll, setRevokingAll] = useState(false)
  const [showImpersonateDialog, setShowImpersonateDialog] = useState(false)
  const [impersonateReason, setImpersonateReason] = useState('')
  const [impersonating, setImpersonating] = useState(false)
  const [updatingAdmin, setUpdatingAdmin] = useState(false)
  const [showPasswordReset, setShowPasswordReset] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [revokeSessionsOnReset, setRevokeSessionsOnReset] = useState(true)
  const [settingPassword, setSettingPassword] = useState(false)
  const [passwordWasSet, setPasswordWasSet] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (isOpen && userId) {
      loadUserDetails()
    }
  }, [isOpen, userId])

  // Reset the password-reset UI whenever the modal closes or switches users
  useEffect(() => {
    setShowPasswordReset(false)
    setNewPassword('')
    setRevokeSessionsOnReset(true)
    setPasswordWasSet(false)
    setCopied(false)
  }, [isOpen, userId])

  const openPasswordReset = () => {
    setNewPassword(generatePassword())
    setPasswordWasSet(false)
    setCopied(false)
    setRevokeSessionsOnReset(true)
    setShowPasswordReset(true)
  }

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(newPassword)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Could not copy to clipboard')
    }
  }

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }

    try {
      setSettingPassword(true)

      const response = await fetch(`/api/admin/users/${userId}/password`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          newPassword,
          revokeSessions: revokeSessionsOnReset
        })
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to reset password')
      }

      const data = await response.json()
      toast.success(data.message || 'Password reset successfully')
      // Keep the password visible so the admin can copy and share it
      setPasswordWasSet(true)
      // Sessions may have been revoked; refresh details to reflect that
      await loadUserDetails()
    } catch (err) {
      logger.error('Error resetting password:', err)
      toast.error(err.message)
    } finally {
      setSettingPassword(false)
    }
  }

  const loadUserDetails = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(`/api/auth/users/${userId}/details`, {
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Failed to load user details')
      }

      const data = await response.json()
      setUserData(data)
    } catch (err) {
      logger.error('Error loading user details:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleRevokeSession = async (sessionId) => {
    try {
      setRevokingSession(sessionId)

      const response = await fetch(`/api/auth/users/${userId}/sessions/${sessionId}`, {
        method: 'DELETE',
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Failed to revoke session')
      }

      toast.success('Session revoked')
      await loadUserDetails()
    } catch (err) {
      logger.error('Error revoking session:', err)
      toast.error(err.message)
    } finally {
      setRevokingSession(null)
    }
  }

  const handleRevokeAllSessions = async () => {
    try {
      setRevokingAll(true)

      const response = await fetch(`/api/auth/users/${userId}/sessions`, {
        method: 'DELETE',
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Failed to revoke sessions')
      }

      const data = await response.json()
      toast.success(data.message)
      await loadUserDetails()
    } catch (err) {
      logger.error('Error revoking all sessions:', err)
      toast.error(err.message)
    } finally {
      setRevokingAll(false)
    }
  }

  const handleImpersonate = async () => {
    try {
      setImpersonating(true)

      const response = await fetch('/api/auth/admin/impersonate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          userId,
          reason: impersonateReason.trim()
        })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to start impersonation')
      }

      const data = await response.json()
      toast.success(data.message)

      // Update localStorage with impersonated user data and session token
      // The API returns the impersonated user in data.user and token in data.session.token
      const authStorage = JSON.parse(localStorage.getItem('auth-storage') || '{}')
      authStorage.state = {
        ...authStorage.state,
        user: data.user,
        token: data.session?.token, // Include token for checkAuth to work
        isImpersonating: true,
        impersonatedUser: data.impersonating
      }
      localStorage.setItem('auth-storage', JSON.stringify(authStorage))

      // Close modal and redirect - page will load with impersonated user
      onClose()
      window.location.href = '/'
    } catch (err) {
      logger.error('Error starting impersonation:', err)
      toast.error(err.message)
    } finally {
      setImpersonating(false)
    }
  }

  const handleToggleAdmin = async () => {
    try {
      setUpdatingAdmin(true)
      const newAdminStatus = !userData.user.isAdmin

      const response = await fetch(`/api/admin/users/${userId}/admin`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isAdmin: newAdminStatus })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to update admin status')
      }

      const data = await response.json()
      toast.success(data.message)
      await loadUserDetails()
    } catch (err) {
      logger.error('Error updating admin status:', err)
      toast.error(err.message)
    } finally {
      setUpdatingAdmin(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleString()
  }

  const parseUserAgent = (userAgent) => {
    if (!userAgent) return 'Unknown'

    // Simple user agent parsing
    if (userAgent.includes('Chrome')) return 'Chrome'
    if (userAgent.includes('Firefox')) return 'Firefox'
    if (userAgent.includes('Safari')) return 'Safari'
    if (userAgent.includes('Edge')) return 'Edge'
    return 'Other'
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">User Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
                <AlertTriangle className="w-5 h-5" />
                <p>{error}</p>
              </div>
            </div>
          ) : userData ? (
            <div className="space-y-6">
              {/* User Info Section */}
              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                    <span className="text-xl font-medium text-gray-700 dark:text-gray-300">
                      {userData.user.firstName && userData.user.lastName
                        ? `${userData.user.firstName.charAt(0)}${userData.user.lastName.charAt(0)}`
                        : userData.user.email?.substring(0, 2).toUpperCase() || '??'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {userData.user.firstName} {userData.user.lastName}
                    </h3>
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <Mail className="w-4 h-4" />
                      <span>{userData.user.email}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {userData.user.isAdmin && (
                        <span className="px-2 py-1 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                          Admin
                        </span>
                      )}
                      {userData.user.isDisabled ? (
                        <span className="px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded">
                          Disabled
                        </span>
                      ) : userData.user.isActive ? (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded">
                          Inactive
                        </span>
                      )}
                      {userData.user.accountType && (
                        <span className="px-2 py-1 text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded">
                          {userData.user.accountType}
                        </span>
                      )}
                      <UserTagBadges userId={userId} />
                    </div>
                    {/* Disabled Reason */}
                    {userData.user.isDisabled && userData.user.disabledReason && (
                      <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <p className="text-sm text-red-700 dark:text-red-300">
                          <strong>Disabled reason:</strong> {userData.user.disabledReason}
                        </p>
                        {userData.user.disabledAt && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                            Disabled on {formatDate(userData.user.disabledAt)}
                          </p>
                        )}
                      </div>
                    )}
                    {/* Admin Toggle */}
                    <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Admin Privileges
                          </span>
                        </div>
                        <button
                          onClick={handleToggleAdmin}
                          disabled={updatingAdmin}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            userData.user.isAdmin
                              ? 'bg-primary-600'
                              : 'bg-gray-300 dark:bg-gray-600'
                          } ${updatingAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              userData.user.isAdmin ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {userData.user.isAdmin
                          ? 'User has admin access to the dashboard'
                          : 'User does not have admin privileges'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Created</span>
                    <p className="text-gray-900 dark:text-gray-100 flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(userData.user.createdAt)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Last Login</span>
                    <p className="text-gray-900 dark:text-gray-100 flex items-center gap-1">
                      <LogIn className="w-4 h-4" />
                      {formatDate(userData.user.lastLoginAt)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Login Count</span>
                    <p className="text-gray-900 dark:text-gray-100">
                      {userData.user.loginCount || 0}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Email Verified</span>
                    <p className="text-gray-900 dark:text-gray-100">
                      {userData.user.emailVerified ? 'Yes' : 'No'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Password / Security Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <KeyRound className="w-4 h-4" />
                  Password
                </h4>
                {!showPasswordReset ? (
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Set a new password for this user and share it with them directly.
                    </p>
                    <button
                      onClick={openPasswordReset}
                      className="ml-3 shrink-0 px-3 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg flex items-center gap-2"
                    >
                      <KeyRound className="w-4 h-4" />
                      Reset Password
                    </button>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3">
                    {passwordWasSet && (
                      <div className="flex items-start gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-green-700 dark:text-green-300">
                          Password updated. Copy it now and share it securely with the user — it
                          won&apos;t be shown again after you close this.
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        New password
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          disabled={settingPassword || passwordWasSet}
                          className="flex-1 px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60"
                          placeholder="Enter or generate a password"
                          autoComplete="new-password"
                        />
                        {!passwordWasSet && (
                          <button
                            type="button"
                            onClick={() => setNewPassword(generatePassword())}
                            disabled={settingPassword}
                            title="Generate a new password"
                            className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={handleCopyPassword}
                          title="Copy password"
                          className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg"
                        >
                          {copied ? (
                            <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        Minimum 6 characters. A strong password is generated by default — edit it if
                        you prefer your own.
                      </p>
                    </div>

                    {!passwordWasSet && (
                      <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={revokeSessionsOnReset}
                          onChange={(e) => setRevokeSessionsOnReset(e.target.checked)}
                          disabled={settingPassword}
                          className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                        />
                        Sign the user out of all active sessions
                      </label>
                    )}

                    <div className="flex gap-2">
                      {passwordWasSet ? (
                        <button
                          onClick={() => setShowPasswordReset(false)}
                          className="flex-1 px-3 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg"
                        >
                          Done
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => setShowPasswordReset(false)}
                            disabled={settingPassword}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleResetPassword}
                            disabled={settingPassword || newPassword.length < 6}
                            className="flex-1 px-3 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {settingPassword ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Setting...
                              </>
                            ) : (
                              'Set Password'
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Organizations Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Organizations ({userData.organizations.length})
                </h4>
                {userData.organizations.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No organizations</p>
                ) : (
                  <div className="space-y-2">
                    {userData.organizations.map((org) => (
                      <div
                        key={org.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{org.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {org.type} &bull; Joined {formatDate(org.joinedAt)}
                          </p>
                        </div>
                        <span className="px-2 py-1 text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded capitalize">
                          {org.role}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sessions Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <Monitor className="w-4 h-4" />
                    Active Sessions ({userData.sessions.length})
                  </h4>
                  {userData.sessions.length > 0 && (
                    <button
                      onClick={handleRevokeAllSessions}
                      disabled={revokingAll}
                      className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                    >
                      {revokingAll ? 'Revoking...' : 'Revoke All'}
                    </button>
                  )}
                </div>
                {userData.sessions.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No active sessions</p>
                ) : (
                  <div className="space-y-2">
                    {userData.sessions.map((session) => {
                      const geoInfo = getGeoDisplayInfo({
                        country: session.geoCountry,
                        countryName: session.geoCountryName,
                        city: session.geoCity,
                        region: session.geoRegion,
                        ipAddress: session.ipAddress
                      })
                      return (
                        <div
                          key={session.id}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {formatIpAddress(session.ipAddress, true)}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                ({parseUserAgent(session.userAgent)})
                              </span>
                            </div>
                            {/* Geo-location info */}
                            <div className="flex items-center gap-2 mt-1">
                              {geoInfo.hasGeo ? (
                                <>
                                  <span className="text-base">{geoInfo.flag}</span>
                                  <span className="text-xs text-gray-600 dark:text-gray-300">
                                    {geoInfo.country}
                                    {(geoInfo.city || geoInfo.region) && (
                                      <span className="text-gray-400 dark:text-gray-500">
                                        {' '}
                                        ({[geoInfo.city, geoInfo.region].filter(Boolean).join(', ')}
                                        )
                                      </span>
                                    )}
                                  </span>
                                </>
                              ) : (
                                <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  Location unknown
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Created: {formatDate(session.createdAt)} &bull; Expires:{' '}
                              {formatDate(session.expiresAt)}
                            </p>
                          </div>
                          <button
                            onClick={() => handleRevokeSession(session.id)}
                            disabled={revokingSession === session.id}
                            className="ml-2 p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                            title="Revoke session"
                          >
                            {revokingSession === session.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Impersonate Dialog */}
              {showImpersonateDialog && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
                    Impersonate User
                  </h4>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mb-3">
                    You will be logged in as this user. Adding a reason is optional but recorded in
                    the audit log.
                  </p>
                  <textarea
                    value={impersonateReason}
                    onChange={(e) => setImpersonateReason(e.target.value)}
                    placeholder="Enter reason for impersonation (optional)..."
                    className="w-full px-3 py-2 text-sm border border-yellow-300 dark:border-yellow-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    rows={2}
                  />
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => {
                        setShowImpersonateDialog(false)
                        setImpersonateReason('')
                      }}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleImpersonate}
                      disabled={impersonating}
                      className="flex-1 px-3 py-2 text-sm bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {impersonating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Starting...
                        </>
                      ) : (
                        'Start Impersonation'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        {userData && !userData.user.isAdmin && !showImpersonateDialog && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setShowImpersonateDialog(true)}
              className="w-full px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg flex items-center justify-center gap-2"
            >
              <UserCog className="w-4 h-4" />
              Impersonate User
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default UserDetailsModal
